import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon } from 'lucide-react';
import { LatexRenderer } from '../../components/ui/LatexRenderer';
interface ReadingPassageProps {
  /** The passage text to display */
  passage: string;
  /** Instruction label, e.g. "Lees de tekst" */
  instruction?: string;
  /** Source attribution, e.g. "Fragment uit 'De Kleine Prins'" */
  source?: string;
  /** Optional label shown above the passage */
  label?: string;
  /** 'stimulus' = shown during question, 'support' = shown after answer */
  mode?: 'stimulus' | 'support';
}
/**
 * ReadingPassage — Visual aid for reading comprehension questions.
 *
 * Renders a styled reading passage card with amber tint, book icon,
 * left border accent, and serif typography. Used as a stimulusAid
 * so the passage is clearly separated from the question text.
 *
 * Seed data format:
 * ```json
 * {
 *   "type": "readingPassage",
 *   "data": {
 *     "passage": "De kat zat op de mat...",
 *     "instruction": "Lees de tekst",
 *     "source": "Fragment uit 'De Kleine Prins'"
 *   }
 * }
 * ```
 */
export function ReadingPassage({
  passage,
  instruction,
  source,
  label,
  mode = 'stimulus'
}: ReadingPassageProps) {
  const isStimulus = mode === 'stimulus';
  return <motion.div initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.35,
    ease: [0.22, 1, 0.36, 1]
  }} className="w-full" data-id="element-9225">
      {/* Instruction header */}
      {(instruction || label) && <div className="flex items-center gap-2 mb-3" data-id="element-9226">
          <BookOpenIcon className="w-4 h-4 text-amber-600" data-id="element-9227" />
          <span className="text-sm font-semibold text-amber-800 uppercase tracking-wide" data-id="element-9228">
            {instruction || label}
          </span>
        </div>}

      {/* Passage card */}
      <div className="relative bg-amber-50/70 border-l-4 border-amber-400 rounded-r-xl overflow-hidden" data-id="element-9229">
        {/* Decorative book icon */}
        <div className="absolute top-3 right-3 opacity-15 pointer-events-none" data-id="element-9230">
          <BookOpenIcon className="w-10 h-10 text-amber-600" data-id="element-9231" />
        </div>

        <div className={isStimulus ? 'p-4 sm:p-5' : 'p-5 sm:p-6 md:p-8'} data-id="element-9232">
          <div className={`
              text-slate-700 leading-relaxed font-serif whitespace-pre-line pr-10
              ${isStimulus ? 'text-base sm:text-lg' : 'text-lg sm:text-xl'}
            `} data-id="element-9233">
            <LatexRenderer content={passage} data-id="element-9234" />
          </div>

          {/* Source attribution */}
          {source && <div className={`mt-3 pt-2 border-t border-amber-200/60 ${isStimulus ? 'text-xs' : 'text-sm'}`} data-id="element-9235">
              <span className="text-amber-700/70 italic" data-id="element-9236">{source}</span>
            </div>}
        </div>
      </div>
    </motion.div>;
}